import { PricingTypes } from '../../../common/constants'
import { getAdminErrorMessage } from '../adminErrorModel'
import { NotAvailable, formatCents, formatVisitDate } from '../adminJobLookupModel'
import {
  InvoiceCheckFinding,
  InvoiceCheckFindingType,
  InvoiceCheckRequest,
  InvoiceCheckResult,
  WorkareaInvoiceCheck,
} from './invoiceCheckerApi'

export type InvoiceCheckFormState = {
  tenantId: number | undefined
  jobId: number | undefined
}

export type FindingRow = {
  key: string
  issue: string
  item: string
  kind: string
  visitDate: string
  invoiceNumbers: string
  invoicedAmount: string
  currentAmount: string
  difference: string
}

export type WorkareaTotal = {
  label: string
  value: string
}

export const defaultInvoiceCheckFinding = (): InvoiceCheckFinding => ({
  type: 'invoiced-amount-differs-from-current-price',
  itemId: null,
  itemName: '',
  isLabor: false,
  visitDate: null,
  invoiceNumbers: [],
  invoicedAmount: null,
  currentAmount: null,
  difference: null,
})

export const defaultWorkareaInvoiceCheck = (): WorkareaInvoiceCheck => ({
  workareaId: 0,
  workareaName: '',
  groupName: '',
  pricingType: PricingTypes.TimeAndMaterials,
  billable: 0,
  invoiced: 0,
  markedInvoiced: 0,
  stillToInvoice: 0,
  unbilledOnInvoiceScreen: 0,
  findings: [],
})

export const defaultInvoiceCheckResult = (): InvoiceCheckResult => ({
  jobId: 0,
  workareas: [],
})

export const toInvoiceCheckRequest = (state: InvoiceCheckFormState): InvoiceCheckRequest | null => {
  if (state.tenantId === undefined || state.jobId === undefined) {
    return null
  }
  return { tenantId: state.tenantId, jobId: state.jobId }
}

export const hasFindings = (workarea: WorkareaInvoiceCheck): boolean => {
  return workarea.findings.length > 0
}

export const sortWorkareasByFindings = (workareas: WorkareaInvoiceCheck[]): WorkareaInvoiceCheck[] => {
  return [
    ...workareas.filter(hasFindings),
    ...workareas.filter(workarea => !hasFindings(workarea)),
  ]
}

export const workareaKey = (workarea: WorkareaInvoiceCheck): string => String(workarea.workareaId)

export const expandedWorkareaKeys = (workareas: WorkareaInvoiceCheck[]): string[] => {
  return workareas.filter(hasFindings).map(workareaKey)
}

export const findingTypeLabel = (type: InvoiceCheckFindingType): string => {
  return {
    'invoiced-amount-differs-from-current-price': 'Invoiced amount differs from current price',
    'marked-invoiced-without-invoice-line': 'Marked invoiced but no invoice line',
    'linked-to-multiple-transactions': 'Linked to more than one transaction',
    'invoice-line-without-item': 'Invoice line with no matching item',
    'over-invoiced': 'Workarea invoiced for more than its billable total',
    'invoiced-more-than-marked-invoiced': 'Workarea invoiced for more than its marked-invoiced items',
  }[type] ?? type
}

const isWorkareaLevel = (finding: InvoiceCheckFinding): boolean => {
  return finding.itemId === null && finding.itemName === ''
}

export const toFindingRows = (findings: InvoiceCheckFinding[]): FindingRow[] => {
  return findings.map((finding, index) => ({
    key: `${index}-${finding.type}-${finding.itemId ?? ''}`,
    issue: findingTypeLabel(finding.type),
    item: isWorkareaLevel(finding) ? 'Workarea total' : finding.itemName || NotAvailable,
    kind: isWorkareaLevel(finding) ? NotAvailable : finding.isLabor ? 'Labor' : 'Item',
    visitDate: formatVisitDate(finding.visitDate),
    invoiceNumbers: finding.invoiceNumbers.length === 0 ? NotAvailable : finding.invoiceNumbers.join(', '),
    invoicedAmount: formatCents(finding.invoicedAmount),
    currentAmount: formatCents(finding.currentAmount),
    difference: formatCents(finding.difference),
  }))
}

export const toWorkareaTotals = (workarea: WorkareaInvoiceCheck): WorkareaTotal[] => {
  return [
    { label: 'Billable', value: formatCents(workarea.billable) },
    { label: 'Invoiced', value: formatCents(workarea.invoiced) },
    { label: 'Already billed (marked invoiced)', value: formatCents(workarea.markedInvoiced) },
    { label: 'Still to invoice', value: formatCents(workarea.stillToInvoice) },
    { label: 'Unbilled on invoice screen', value: formatCents(workarea.unbilledOnInvoiceScreen) },
  ]
}

export const findingCountLabel = (workarea: WorkareaInvoiceCheck): string => {
  const count = workarea.findings.length
  if (count === 0) {
    return 'No issues'
  }
  return `${count} ${count === 1 ? 'finding' : 'findings'}`
}

export const getResultSummary = (result: InvoiceCheckResult): string => {
  const total = result.workareas.length
  if (total === 0) {
    return 'No workareas were returned for this job.'
  }
  const withFindings = result.workareas.filter(hasFindings).length
  const noun = total === 1 ? 'checked workarea' : 'checked workareas'
  if (withFindings === 0) {
    return `No discrepancies found in ${total} ${noun}.`
  }
  return `${withFindings} of ${total} ${noun} ${withFindings === 1 ? 'has' : 'have'} findings.`
}

export const getInvoiceCheckErrorMessage = (error: unknown): string => {
  return getAdminErrorMessage(error, 'Failed to check the job. Please try again.')
}
